import { cn } from '@/lib/utils';
import { cva } from 'class-variance-authority';

/**
 * Button only theme color variants
 * Includes text, background, and border colors and hover states.
 */
const themeColor = {
  white: cn(
    'text-neutral-0',
    'visited:text-olive-400',
    'aria-disabled:text-neutral-500 disabled:text-neutral-500'
  ),
  hudsonNavy: cn(
    'text-blue-900 hover:text-blue-700',
    'active:text-blue-900 visited:text-olive-800'
  ),
  hugoBlueShade: cn(
    'text-blue-700 hover:text-blue-900',
    'active:text-blue-900 visited:text-olive-800'
  ),
  midGray: 'text-neutral-400',
  darkGray: 'text-neutral-500',
};

/**
 * Button underline variants
 * Includes hover and active states.
 */
const underline = {
  true: 'underline hover:no-underline active:no-underline',
  false: 'no-underline hover:underline active:underline',
};

/**
 * Button only text size variants
 */
const textSize = {
  xs: 'text-xs/150',
  sm: 'text-sm/150',
  md: 'text-base/150', // 16 px
  lg: 'text-lg/150',
  xl: 'text-xl/150',
};

/**
 * Button and Link type variants
 * Includes padding, font weight, and text size.
 * Inline and standalone are primarily used for link types
 * Mini and lg are primarily used for button types
 */
const mode = {
  inline: ['p-0 font-normal', textSize.md],
  standalone: ['p-2 font-bold', textSize.md],
  mini: ['p-3', textSize.md],
  lg: ['py-3 px-5', textSize.xl],
};

/**
 * Buttons are available in two border sizes
 * Base Buttons are styled with border 2
 * Links and text are styled with border 1
 */
const borderSize = {
  1: 'border-1',
  2: 'border-2',
};

/**
 * Button border color variants
 * Includes border color and hover states.
 * Transparent is used for all buttons except secondary.
 */
const borderColor = {
  hudsonNavy: 'border-blue-900 hover:blue-700',
  transparent: 'border-transparent',
  midGray: 'border-neutral-400',
};

/**
 * Button border radius variants
 * Base buttons use lg
 * Links and text use md
 */
const borderRadius = {
  md: 'rounded-md',
  lg: 'rounded-lg',
};

export const buttonVariants = cva(
  cn(
    'inline-flex items-center justify-center rounded-lg border-transparent',
    'font-effra text-xl font-bold leading-150 py-3 px-5 tracking-normal gap-4 w-fit',
    'focus-visible:outline-hidden focus-visible:border-azure-500 focus-visible:shadow-ring',
    'disabled:pointer-events-none',
    'transition duration-200 ease-in-out'
  ),
  {
    variants: {
      variant: {
        primary: cn(
          'bg-blue-900 hover:bg-blue-700 active:bg-blue-900 visited:text-neutral-0',
          themeColor.white,
          underline.false,
          mode.lg,
          borderSize['2'],
          borderColor.transparent,
          borderRadius.lg
        ),
        secondary: cn(
          'bg-neutral-0 hover:border-blue-700 active:border-blue-900 visited:text-blue-900',
          themeColor.hudsonNavy,
          underline.false,
          mode.lg,
          borderSize['2'],
          borderColor.hudsonNavy,
          'aria-disabled:border-neutral-400 disabled:border-neutral-400',
          borderRadius.lg
        ),
        cta: cn(
          'bg-tomato-600 hover:bg-tomato-700 active:bg-tomato-700 visit:text-neutral-0',
          themeColor.white,
          underline.false,
          mode.lg,
          borderSize['2'],
          borderColor.transparent,
          borderRadius.lg
        ),
        text: cn(
          'bg-inherit',
          themeColor.hudsonNavy,
          underline.true,
          mode.standalone,
          borderSize['1'],
          borderColor.transparent,
          borderRadius.md
        ),
        link: cn(
          'bg-inherit',
          themeColor.hugoBlueShade,
          underline.true,
          mode.inline,
          borderSize['1'],
          borderColor.transparent,
          borderRadius.md,
          'aria-disabled:text-neutral-400'
        ),
      },
    },
    defaultVariants: {
      variant: 'primary',
    },
    compoundVariants: [
      {
        // Disabled state for fill background variants
        variant: ['cta', 'primary'],
        className: 'disabled:bg-neutral-400 disabled:text-neutral-0',
      },
      {
        // Disabled state for no fill background variants
        variant: ['secondary', 'text', 'link'],
        className: 'disabled:text-neutral-400 disabled:bg-neutral-0',
      },
      {
        variant: ['cta', 'primary', 'secondary', 'text'],
        className: 'focus-visible:underline',
      },
    ],
  }
);
